import React, { useEffect } from "react";

import { useTranslation } from "react-i18next";
import { Modal, Popover } from "antd";

import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { Button } from "@companion-core/shared/app/Components/Button";
import { useNewsletterModal } from "./hooks/useNewsletterModal";
import { NewsletterModalProps } from "./newsletterModal.model";
import { useMediaQuery } from "@companion-core/web/src/Utilities/mediaQuery";
import { lockScroll, unlockScroll } from "@companion-core/web/src/Utilities/scroll";
import { useNewsletterReminder } from "@companion-core/shared/app/Components/NewsletterModal/hooks/useNewsletterReminder";
import { redirectTo } from "@companion-core/web/src/Utilities/navigation";

import "@companion-core/web/src/Assets/Styles/scss/newsletterModal.scss";

const CloseIcon = ({ onClose }: { onClose: () => void }) => (
  <div className="newsletter__close-background" onClick={onClose} data-testid="newsletter__close">
    <FontIcon
      name="JP-Close-bold"
      className="newsletter__close"
      size={24}
      data-testid="newsletter__close-icon"
    />
  </div>
);

export const NewsletterModalContent = ({
  img,
  subscribe,
  onClose,
  isUpdating,
}: NewsletterModalProps) => {
  const { t } = useTranslation();

  return (
    <div className="newsletter__content" data-testid="newsletter__content">
      <div className="newsletter__image-wrapper" data-testid="newsletter__image-wrapper">
        <img src={img} className="newsletter__image" />
        <CloseIcon onClose={onClose} />
      </div>
      <div className="newsletter__text-content" data-testid="newsletter__text-content">
        <span className="newsletter__title title-1">{t("newsletter.title")}</span>
        <span className="newsletter__text body-text-6">{t("newsletter.text")}</span>
      </div>
      <Button
        size="large"
        type="primary"
        classNames={["newsletter__cta"]}
        label={t("newsletter.cta")}
        testID="newsletter__cta"
        onClick={subscribe}
        loading={isUpdating}
      />
    </div>
  );
};

export const NewsletterModal = () => {
  const isMobileSize = useMediaQuery("(max-width: 768px)");
  const { isOpen } = useNewsletterReminder();
  const { img, subscribe, onClose, isUpdating, myAccountNewsletterModule, memberId } =
    useNewsletterModal();
  useEffect(() => {
    isOpen ? lockScroll() : unlockScroll();
  }, [isMobileSize, isOpen]);

  const redirectToNewsletter = async () => {
    redirectTo(
      myAccountNewsletterModule.link?.replace("[PUID]", memberId ?? "") ?? "",
      "external",
      undefined,
      "_blank"
    );
  };

  return isMobileSize ? (
    <Popover
      open={isOpen}
      classNames={{ root: "newsletter--mobile" }}
      arrow={false}
      autoAdjustOverflow={false}
      content={
        <NewsletterModalContent
          img={img}
          subscribe={myAccountNewsletterModule?.type === "cta" ? redirectToNewsletter : subscribe}
          onClose={onClose}
          isUpdating={isUpdating}
        />
      }
    />
  ) : (
    <Modal
      open={isOpen}
      footer={<></>}
      width={"100%"}
      className="newsletter"
      closeIcon={<CloseIcon onClose={onClose} />}
      onCancel={onClose}>
      <NewsletterModalContent
        img={img}
        subscribe={myAccountNewsletterModule?.type === "cta" ? redirectToNewsletter : subscribe}
        onClose={onClose}
        isUpdating={isUpdating}
      />
    </Modal>
  );
};
